import { Box, Typography, Card, Stack, ListItem, ListItemAvatar, Avatar, ListItemText, Table, TableBody, TableRow, TableCell, IconButton, Tooltip } from "@mui/material";
import { getFirestore, getDoc, doc } from "firebase/firestore";
import { DateTime } from "luxon";
import { useState, useEffect } from "react";
import passportRequestConverter from "../../converters/passportRequestConverter";
import { Employee } from "../../types/Employee";
import { PassportRequest, PassportRequestStatus } from "../../types/PassportRequest";
import { queryEmployee } from "../../utils/queryEmployee";
import { Notification } from "../../types/Notification";
import { EnhancedTableCell } from "../EnhancedTableCell";
import { RunCircleRounded, MopedRounded, CheckCircleRounded } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { MedicalPaperRequest, MedicalPaperRequestStatus } from "../../types/MedicalPaperRequest";
import medicalPaperRequestConverter from "../../converters/medicalPaperRequestConverter";
import { NotificationEmployeeHeading } from "./NotificationEmployeeHeading";
import { updateMedicalPaperRequestStatus } from "../../utils/updateMedicalPaperRequest";
import { MedicalPaperRequestInfoBox } from "../dashboard/medical/MedicalPaperRequestInfoBox";
import { RequestActionBox } from "../RequestActionBox";

export const NotificationMedicalPaperRequest = (props: { notification: Notification }) => {
  const { notification } = props;
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [medicalPaperRequest, setMedicalPaperRequest] = useState<MedicalPaperRequest>();
  const [employee, setEmployee] = useState<Employee>();

  const db = getFirestore();

  const handleUpdateMedicalPaperRequest = (s: MedicalPaperRequestStatus) => {
    if (medicalPaperRequest) {
      setUpdating(true);
      updateMedicalPaperRequestStatus(medicalPaperRequest, s).then(() => {
        let message = `Employee '${medicalPaperRequest.idNumber}' medical paper request has been update.`;
        if (s === 'Processing')
          message = `Employee '${medicalPaperRequest.idNumber}' medical paper request is now being processed.`;
        else if (s === 'Available')
          message = `Employee '${medicalPaperRequest.idNumber}' medical paper request is now ready for pickup.`;
        else if (s === 'Completed')
          message = `Employee '${medicalPaperRequest.idNumber}' medical paper request is now tagged as complete.`;
        else if (s === 'Cancelled')
          message = `Employee '${medicalPaperRequest.idNumber}' medical paper request is now cancelled.`;

        setMedicalPaperRequest({ ...medicalPaperRequest, status: s });
        enqueueSnackbar(message, { variant: 'success' });

        setUpdating(false);
      })
    }
  };

  useEffect(() => {
    if (medicalPaperRequest?.idNumber !== employee?.idNumber) {
      setLoading(true);
      if (medicalPaperRequest !== undefined) {
        queryEmployee(medicalPaperRequest.idNumber).then(e => {
          setEmployee(e);
          setLoading(false);
        });
      }
    }
  }, [medicalPaperRequest]);
  
  useEffect(() => {
    // Get passport request
    if (notification.pathToDocument) {
      getDoc(doc(db, notification.pathToDocument).withConverter(medicalPaperRequestConverter)).then(qDocSnapshot => {
        if (qDocSnapshot.exists()) {
          setMedicalPaperRequest(qDocSnapshot.data());
        }
      });
    }
  }, []);

  return (
    <Box>
      <Typography variant='h4' mb={1}>Medical Paper Request</Typography>
      <Card>
        {loading ? (
          <Box p={1}>
            <Typography>Please wait...</Typography>
          </Box>
        ) : (
          <Box p={1}>
            <Stack direction='row' mb={2}>
              {employee && <NotificationEmployeeHeading employee={employee} />}
              {medicalPaperRequest && (<RequestActionBox status={medicalPaperRequest.status} onUpdateStatus={handleUpdateMedicalPaperRequest} isUpdating={updating} />)}
            </Stack>
            {medicalPaperRequest && (
              <MedicalPaperRequestInfoBox medicalPaperRequest={medicalPaperRequest} />
            )}
          </Box>
        )}
      </Card>
    </Box>
  );
};